import { Target, FileText, Link2, GitBranch, TrendingUp, TrendingDown, AlertTriangle, ShieldAlert, Layers } from 'lucide-react';
import StockBreadcrumb from '../StockBreadcrumb';
import PlatformStockHeader from '../PlatformStockHeader';
import StockSectionNav from '../StockSectionNav';
import ProjectionGrid from '../ProjectionGrid';
import DriversCard from '../DriversCard';
import ForecastCard from '../ForecastCard';
import SourcesList from '../SourcesList';
import OrbitGrid from '../OrbitGrid';
import BriefCallout from '../BriefCallout';

const STOCK_DATA = {
  NVDA: {
    name: 'Nvidia Corporation', tag: 'Semiconductors · AI',
    price: '892.41', change: '+2.31% today', direction: 'up',
    periods: [
      { key: 'today', label: 'Today',      range: '$910–$935', verdict: 'hit' },
      { key: 'week',  label: 'Week',       range: '$920–$955', verdict: 'pend' },
      { key: 'month', label: 'Month',      range: '$940–$1,000', verdict: 'pend' },
      { key: 'year',  label: 'Year',       range: '$1,050–$1,200', verdict: 'pend' },
    ],
    projections: [
      { key: 'today', label: "Today's Final", date: 'Closed May 18', range: '$910 — $935', verdict: 'hit', actual: '$924.10', scenarios: [{ variant: 'bull', range: '>$935', prob: 12 }, { variant: 'base', range: '$910–$935', prob: 68, hit: true }, { variant: 'bear', range: '<$910', prob: 20 }], confidence: 85 },
      { key: 'week',  label: 'This Week',     date: 'Closes May 23', range: '$920 — $955', verdict: 'pend', scenarios: [{ variant: 'bull', range: '>$955', prob: 18 }, { variant: 'base', range: '$920–$955', prob: 62 }, { variant: 'bear', range: '<$920', prob: 20 }], confidence: 72 },
      { key: 'month', label: 'This Month',    date: 'Closes May 31', range: '$940 — $1,000', verdict: 'pend', scenarios: [{ variant: 'bull', range: '>$1,000', prob: 24 }, { variant: 'base', range: '$940–$1,000', prob: 54 }, { variant: 'bear', range: '<$940', prob: 22 }], confidence: 62 },
      { key: 'year',  label: 'This Year',     date: 'Closes Dec 31', range: '$1,050 — $1,200', verdict: 'pend', scenarios: [{ variant: 'bull', range: '>$1,200', prob: 24 }, { variant: 'base', range: '$1,050–$1,200', prob: 48 }, { variant: 'bear', range: '<$1,050', prob: 28 }], confidence: 55 },
    ],
    drivers: [
      { sentiment: 'bull', title: 'Meta confirmed AI capex $36–40B for 2026, naming Blackwell explicitly', detail: 'Removes near-term capex uncertainty. Strongest single signal in today\'s report.', periods: ['Today ✓', 'This Week'], claim: 'fact' },
      { sentiment: 'bull', title: 'Goldman Sachs raises price target to $1,100 · Buy rating', detail: 'Analyst consensus repricing upward. Technical support now at $910.', periods: ['Today ✓', 'This Week', 'This Month'], claim: 'fact' },
      { sentiment: 'watch', title: 'Enterprise AI software revenue — MSFT, GOOGL earnings this week', detail: 'Critical missing data. If AI features not monetizing, capex slowdown narrative resurfaces.', periods: ['All periods'], claim: 'missing_data' },
    ],
    forecast: { issueRef: 'Issue #103', projected: '$885–$902', actual: '$892.41 ✓', direction: 'hit', error: '+0.8%', attribution: 'TSMC supply confirmation (CoWoS easing) + Goldman prior-day note provided the upward bias. AWS cloud beat added demand confidence.' },
    sources: [
      { color: '#09090B', initial: 'S', publisher: 'SEC EDGAR', title: 'NVDA 10-Q Filing — Q1 FY2026', credibility: 100, meta: 'Primary filing · sec.gov · 3d ago' },
      { color: '#09090B', initial: 'S', publisher: 'SEC EDGAR', title: 'META 10-Q — AI Capex Guidance Commentary', credibility: 100, meta: 'Primary filing · sec.gov · 1d ago' },
      { color: '#2563EB', initial: 'A', publisher: 'Alpaca Market Data', title: 'NVDA daily bar · close $892.41 · vol 52.1M shares', credibility: 95, meta: 'Market data · 1d ago' },
      { color: '#E04E1A', initial: 'R', publisher: 'Reuters', title: 'Meta raises AI capex guidance to $40B for 2026', credibility: 65, meta: 'News · 2h ago' },
      { color: '#0a0a0a', initial: 'B', publisher: 'Bloomberg', title: 'Goldman Sachs raises NVDA price target to $1,100 · Buy', credibility: 65, meta: 'Analyst revision · 18h ago' },
    ],
    orbit: [
      { sym: 'AMD',  name: 'Adv. Micro Devices', change: '−0.4%', direction: 'dn', relation: 'Direct Competitor', desc: 'MI300X +40% Q2 guidance. Hyperscalers trialing AMD as a partial NVDA alternative. Moat erosion narrative gaining weight.', signalBg: 'var(--amber-s)', signalColor: 'var(--amber)', signalLabel: 'Monitor: market share risk' },
      { sym: 'TSMC', name: 'Taiwan Semiconductor', change: '+1.2%', direction: 'up', relation: "NVDA's Fab · CoWoS", desc: 'Blackwell on-track confirmed. CoWoS constraints easing. Leading indicator for NVDA supply chain health.', signalBg: 'var(--green-s)', signalColor: 'var(--green)', signalLabel: 'Positive: confirms supply' },
      { sym: 'MSFT', name: 'Microsoft Corporation', change: '+0.8%', direction: 'up', relation: 'Biggest customer + custom silicon threat', desc: 'Maia chip progressing. If Azure AI workloads shift to Maia in 2027, NVDA loses its biggest hyperscaler customer.', signalBg: 'var(--amber-s)', signalColor: 'var(--amber)', signalLabel: 'Watch: custom silicon timeline' },
      { sym: 'ASML', name: 'ASML Holding N.V.', change: '+0.9%', direction: 'up', relation: 'EUV lithography enables NVDA chips', desc: "ASML's EUV machines are essential for TSMC Blackwell production. Strong ASML order book signals 12–18 month forward AI chip demand.", signalBg: 'var(--green-s)', signalColor: 'var(--green)', signalLabel: 'Positive: forward demand signal' },
    ],
    bullCase: { headline: 'The data center capex cycle is still in its early innings', body: "Nvidia's <strong>Blackwell architecture</strong> has secured backlog extending well into 2026, with all four major hyperscalers accelerating AI infrastructure spending. Demand is not slowing.<br/><br/>Every major enterprise software company is rebuilding around AI inference — sustained GPU compute demand for 18–24 months minimum. <strong>Nvidia captures ~85% of that spend.</strong><br/><br/>Goldman $1,100 Buy · MS $980 Neutral · Wedbush $1,025 Outperform.", signals: [{ up: true, text: '<strong>Signal:</strong> Blackwell shipments confirmed on schedule by TSMC. CoWoS packaging constraints easing through Q3 2026.' }, { up: true, text: '<strong>Signal:</strong> Meta Q1 confirmed AI capex at $36–40B for 2026 — all Nvidia silicon. No capex pullback signaled.' }] },
    bearCase: { headline: 'The valuation prices in perfection — markets rarely deliver that', body: "At <strong>35× forward sales</strong>, there is no margin for a guidance miss, a delay, or a shift in hyperscaler priorities. Any one of those events triggers significant multiple compression.<br/><br/><strong>AMD's MI300X</strong> is gaining real traction in hyperscaler trials. Microsoft and Google are both accelerating custom silicon (Maia, TPU v5) that could reduce Nvidia dependence by 2027.<br/><br/><strong>China revenue ~20% of total.</strong> Further export controls could clip 8–12% of revenue before year-end.", signals: [{ up: false, text: '<strong>Risk:</strong> AMD MI300X shipment guidance raised 40% for Q2. Hyperscaler diversification accelerating faster than expected.' }, { up: false, text: '<strong>Risk:</strong> Export control expansion expected by Q3 2026. China-related revenue directly at risk.' }] },
    risks: [
      { sev: 'rs-hi',  title: 'HIGH — Valuation, 35× forward sales', desc: 'Any guidance miss triggers multiple compression. Threatens This Month and This Year projections immediately.' },
      { sev: 'rs-hi',  title: 'HIGH — AMD MI300X competition',      desc: 'Real workload migration happening. MSFT & GOOG custom silicon on 12-month roadmap. Moat erosion by 2027.' },
      { sev: 'rs-med', title: 'MEDIUM — Export controls, China ~20%', desc: 'Further restrictions could clip 8–12% of revenue by end of This Month.' },
      { sev: 'rs-lo',  title: 'LOW — Supply chain / TSMC capacity',  desc: 'Secured through H2 2026. CoWoS constraints easing. No near-term impact on any projection.' },
    ],
  },
};

const FALLBACK = (sym) => ({
  name: sym, tag: 'Semiconductors', price: '—', change: '—', direction: 'up',
  periods: [], projections: [], drivers: [], forecast: null,
  sources: [], orbit: [], bullCase: null, bearCase: null, risks: [],
});

const SECTIONS = [
  { id: 'ss-projections', label: 'Projections', icon: Target },
  { id: 'ss-research',    label: 'Research',    icon: FileText },
  { id: 'ss-sources',     label: 'Sources',     icon: Link2 },
  { id: 'ss-orbit',       label: 'In Orbit',    icon: GitBranch },
];

export default function StockDiveView({ sym = 'NVDA', onBack, onSelectStock }) {
  const d = STOCK_DATA[sym] ?? FALLBACK(sym);

  return (
    <>
      <StockBreadcrumb sym={sym} onBack={onBack} />

      <PlatformStockHeader
        sym={sym} name={`${d.name} · Nasdaq: ${sym}`} tag={d.tag}
        price={d.price} change={d.change} direction={d.direction}
        periods={d.periods}
      />

      <StockSectionNav sections={SECTIONS} scrollContainerId="stock-scroll" />

      <div className="stock-scroll" id="stock-scroll">

        {/* PROJECTIONS */}
        <div className="stock-section" id="ss-projections">
          <div className="sec-tag"><Target size={13} />Projection Engine</div>
          <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', marginBottom: 10 }}>Four period finals.<br /><em>Every close. Every stock.</em></h2>
          <p className="section-lead" style={{ marginBottom: 28 }}>Published at market open. Resolved publicly — Hit, Beat, or Miss — at every period close.</p>
          <ProjectionGrid periods={d.projections} />
          <div className="sub-lbl"><Layers size={14} />What's driving these projections</div>
          <DriversCard sym={sym} date="May 19" drivers={d.drivers} />
          <div className="sub-lbl"><Target size={14} />Forecast Accountability — Yesterday</div>
          {d.forecast && <ForecastCard sym={sym} {...d.forecast} />}
        </div>

        {/* RESEARCH */}
        <div className="sec-div" />
        <div className="stock-section" id="ss-research">
          <div className="sec-tag"><FileText size={13} />Research · {sym} · May 19, 2026</div>
          <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', marginBottom: 10 }}>Not headlines.<br /><em>Real research.</em></h2>
          <p className="section-lead" style={{ marginBottom: 36 }}>Bull case, bear case, Devil's Advocate, and risk signals — every market day.</p>

          {(d.bullCase || d.bearCase) && (
            <div className="bull-bear" style={{ marginBottom: 24 }}>
              {d.bullCase && (
                <div className="case-card">
                  <div className="case-card-head bull">
                    <TrendingUp size={16} /><span className="case-tag">Bull Case</span>
                  </div>
                  <div className="case-body">
                    <div className="case-headline">{d.bullCase.headline}</div>
                    <p dangerouslySetInnerHTML={{ __html: d.bullCase.body }} />
                    <div className="case-signals">
                      {d.bullCase.signals.map((s, i) => (
                        <div key={i} className="signal-row">
                          <TrendingUp size={14} style={{ color: 'var(--green)', flexShrink: 0, marginTop: 2 }} />
                          <span dangerouslySetInnerHTML={{ __html: s.text }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {d.bearCase && (
                <div className="case-card">
                  <div className="case-card-head bear">
                    <TrendingDown size={16} /><span className="case-tag">Bear Case</span>
                  </div>
                  <div className="case-body">
                    <div className="case-headline">{d.bearCase.headline}</div>
                    <p dangerouslySetInnerHTML={{ __html: d.bearCase.body }} />
                    <div className="case-signals">
                      {d.bearCase.signals.map((s, i) => (
                        <div key={i} className="signal-row">
                          <AlertTriangle size={14} style={{ color: 'var(--red)', flexShrink: 0, marginTop: 2 }} />
                          <span dangerouslySetInnerHTML={{ __html: s.text }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <BriefCallout title="Devil's Advocate — What could break the thesis before the period closes?" icon={AlertTriangle}>
            <p>The bull case rests on a crucial assumption: that enterprise AI adoption will translate into sustained GPU revenue at the scale currently being spent. Every major technology infrastructure cycle included a period of over-building. <strong>The fiber-optic boom, the cloud buildout of 2014–16, the crypto mining cycle</strong> — each ended the same way.</p>
            <p>Today's hyperscalers are spending $150B+ annually on AI infrastructure. The question is not whether AI will matter. The question is whether <strong>revenue generated by AI compute will justify the capital deployed within the investment horizon of current equity valuations.</strong></p>
            <p><strong>What to watch:</strong> Enterprise AI software revenue at MSFT, GOOGL, and Salesforce next earnings.</p>
          </BriefCallout>

          <div style={{ fontFamily: 'var(--fh)', fontSize: 12, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.06em', textTransform: 'uppercase', padding: '14px 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={14} style={{ color: 'var(--amber)' }} /> Risk Assessment
          </div>
          <div className="risk-grid">
            {d.risks.map((r, i) => (
              <div key={i} className="risk-item">
                <div className={`risk-sev-dot ${r.sev}`} />
                <div className="risk-text">
                  <div className="risk-title">{r.title}</div>
                  <div className="risk-desc">{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SOURCES */}
        <div className="sec-div" />
        <div className="stock-section" id="ss-sources">
          <div className="sec-tag"><Link2 size={13} />Sources</div>
          <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', marginBottom: 10 }}>{d.sources.length} citations.<br /><em>Every claim traceable.</em></h2>
          <SourcesList sources={d.sources} />
        </div>

        {/* IN ORBIT */}
        <div className="sec-div" />
        <div className="stock-section" id="ss-orbit">
          <div className="sec-tag"><GitBranch size={13} />In Orbit</div>
          <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', marginBottom: 10 }}>Connected stocks.<br /><em>Same ecosystem, different angles.</em></h2>
          <p className="section-lead" style={{ marginBottom: 28 }}>Suppliers, customers, and competitors whose moves directly affect the {sym} thesis — tracked in today's issue.</p>
          <OrbitGrid stocks={d.orbit} onSelectStock={onSelectStock} />
        </div>

      </div>
    </>
  );
}
